import React, { useState } from 'react';
import { MapPin } from 'lucide-react';
import { getFreightDestinationOptions } from '../../utils/freightDestinations.js';
import './FreightDestinationSelect.css';

const CUSTOM_SENTINEL = '__custom__';

// Per-item freight destination picker. Beyond the account's saved
// destinations, lets the user type a one-off label + address (e.g. a
// specific upholsterer) that's attached to just this item — not saved to
// settings. The address is what "Shipping To" reports pull from, so a
// custom pick isn't just cosmetic text.
export default function FreightDestinationSelect({
  value,
  address = '',
  onChange,
  freightDestinations = [],
  disabled = false,
  className = '',
}) {
  const [showModal, setShowModal] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customAddress, setCustomAddress] = useState('');

  const options = getFreightDestinationOptions(freightDestinations);
  const isKnown = options.some((o) => o.value === value);
  const displayOptions = value && !isKnown ? [...options, { value, label: value }] : options;

  function openModal() {
    setCustomLabel(!isKnown ? value || '' : '');
    setCustomAddress(!isKnown ? address || '' : '');
    setShowModal(true);
  }

  function handleChange(e) {
    const next = e.target.value;
    if (next === CUSTOM_SENTINEL) {
      openModal();
      return;
    }
    onChange(next, '');
  }

  function saveCustom() {
    const label = customLabel.trim();
    if (!label) return;
    onChange(label, customAddress.trim());
    setShowModal(false);
  }

  return (
    <>
      <select className={className} value={value || ''} disabled={disabled} onChange={handleChange}>
        {displayOptions.map((d) => (
          <option key={d.value} value={d.value}>
            {d.label}
          </option>
        ))}
        {!disabled && <option value={CUSTOM_SENTINEL}>+ Custom destination…</option>}
      </select>

      {!isKnown && address && (
        <button
          type="button"
          className="fd-select-address-hint"
          onClick={openModal}
          disabled={disabled}
          title="Edit custom destination address"
        >
          <MapPin size={11} />
          <span>{address}</span>
        </button>
      )}

      {showModal && (
        <div className="fd-select-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="fd-select-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Custom Freight Destination</h4>
            <p>A one-off destination for just this item — not added to your saved list.</p>

            <label className="fd-select-modal-label">Label</label>
            <input
              autoFocus
              type="text"
              placeholder="e.g. Bob's Upholstery"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowModal(false);
              }}
            />

            <label className="fd-select-modal-label">Address / Ship-To Notes</label>
            <textarea
              rows={3}
              placeholder="e.g. 456 Oak St, Springfield, IL 62704 — Attn: Bob"
              value={customAddress}
              onChange={(e) => setCustomAddress(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowModal(false);
              }}
            />
            <p className="fd-select-modal-hint">
              This is what shows up in Shipping To on reports for this item.
            </p>

            <div className="fd-select-modal-actions">
              <button type="button" className="fd-select-cancel-btn" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="fd-select-save-btn"
                onClick={saveCustom}
                disabled={!customLabel.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
